import { plainTextFromMarkdown, truncatePlainText } from "./markdown-plain.js";

const TASK_TITLE_MAX_CHARS = 80;

type TeamBot = {
  id: string;
  archivedAt: string | null;
  /** Missing means the caller owns the bot, as on older servers and mobile's bot list. */
  access?: "owner" | "manage" | "chat";
};

/** A one-line task title from a message's first line; empty when the message has no text. */
export function messageTaskTitle(text: string): string {
  const line = text.split("\n").find((candidate) => candidate.trim()) ?? "";
  const plain = plainTextFromMarkdown(line).trim();
  if (plain.length <= TASK_TITLE_MAX_CHARS) return plain;
  const cut = truncatePlainText(plain, TASK_TITLE_MAX_CHARS - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > TASK_TITLE_MAX_CHARS / 2 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

/**
 * Who a task made from a message belongs to: the bot that wrote it, or for the owner's own
 * message the bot whose chat it is in. Tickets always need an assignee the caller owns, so a
 * group message from the owner, or a bot someone else shares, has none.
 */
export function messageTaskAssignee(
  message: { role: string; botId?: string },
  chatBotId: string | undefined,
  bots: readonly TeamBot[],
): string | undefined {
  const id = message.role === "bot" ? message.botId : chatBotId;
  const bot = id ? bots.find((candidate) => candidate.id === id) : undefined;
  if (!bot || bot.archivedAt) return undefined;
  return bot.access === undefined || bot.access === "owner" ? bot.id : undefined;
}

/** Active bots a message can be handed to: everyone except the chat's bot and the author. */
export function messageAskBots<T extends TeamBot>(
  bots: readonly T[],
  exclude: readonly (string | undefined)[],
): T[] {
  return bots.filter((bot) => !bot.archivedAt && !exclude.includes(bot.id));
}

/** The message as a Markdown quote that opens another bot's draft, above anything already there. */
export function messageQuoteDraft(text: string, existingDraft = ""): string {
  const quote = text
    .trim()
    .split("\n")
    .map((line) => (line.trim() ? `> ${line}` : ">"))
    .join("\n");
  return existingDraft.trim() ? `${quote}\n\n${existingDraft}` : `${quote}\n\n`;
}
